import { create } from 'zustand';
import { PrettyMerge } from '../lib/tsUtilityTypes';
import { OrderType } from '../types/orders';

type SimpleModalState = 'open' | 'closed';
type SettingModalState = 'closed' | 'one-click' | 'renew';
type ModalStateWithProduct = { productId: string | undefined; openOrClosed: SimpleModalState };
type ModalStateWithPosition = { positionId: string | undefined; openOrClosed: SimpleModalState };

type ClosePositionModalState = PrettyMerge<ModalStateWithProduct, { orderType: OrderType }>;

export type DepositModalContext = 'Onboard' | 'Portfolio';
type DepositModalState =
  | { openOrClosed: 'closed' }
  | { openOrClosed: 'open'; context: DepositModalContext };

export type ModalState = {
  termsModal: SimpleModalState;
  settingsModal: SettingModalState;
  depositModal: DepositModalState;
  signingModal: SimpleModalState;
  withdrawalModal: SimpleModalState;
  leverageModal: SimpleModalState;
  marketsModal: SimpleModalState;
  closeAllPositionsModal: SimpleModalState;
  cancelAllOrdersModal: SimpleModalState;
  closePositionModal: ClosePositionModalState;
  confirmOrderModal: SimpleModalState;
  positionFillsModal: ModalStateWithPosition;
  positionStopOrderModal: ModalStateWithPosition;
  tifModal: SimpleModalState;
  pnlCardModal: ModalStateWithPosition;
  accountModal: SimpleModalState;

  setTermsModal: (termsModal: SimpleModalState) => void;
  setDepositModal: (depositModal: DepositModalState) => void;
  setSigningModal: (signingModal: SimpleModalState) => void;
  setWithdrawalModal: (signingModal: SimpleModalState) => void;
  setSettingsModal: (settingsModal: SettingModalState) => void;
  setLeverageModal: (leverageModal: SimpleModalState) => void;
  setCloseAllPositionsModal: (closePositionModal: SimpleModalState) => void;
  setCancelAllOrdersModal: (cancelAllOrdersModal: SimpleModalState) => void;
  setClosePositionModal: (closePositionModal: ClosePositionModalState) => void;
  setMarketsModal: (marketsModal: SimpleModalState) => void;
  setPositionFillsModal: (positionFillsModal: ModalStateWithPosition) => void;
  setPositionStopOrderModal: (positionStopOrderModal: ModalStateWithPosition) => void;
  setConfirmOrderModal: (confirmOrderModal: SimpleModalState) => void;
  setTifModal: (tifModal: SimpleModalState) => void;
  setPnlCardModal: (pnlCardModal: ModalStateWithPosition) => void;
  setAccountModal: (accountModal: SimpleModalState) => void;
};

export const useModalState = create<ModalState>((set) => ({
  termsModal: 'closed',
  settingsModal: 'closed',
  depositModal: { openOrClosed: 'closed' },
  signingModal: 'closed',
  withdrawalModal: 'closed',
  leverageModal: 'closed',
  marketsModal: 'closed',
  closeAllPositionsModal: 'closed',
  cancelAllOrdersModal: 'closed',
  closePositionModal: { productId: undefined, openOrClosed: 'closed', orderType: 'MARKET' },
  positionFillsModal: { positionId: undefined, openOrClosed: 'closed' },
  positionStopOrderModal: { positionId: undefined, openOrClosed: 'closed' },
  tifModal: 'closed',
  confirmOrderModal: 'closed',
  pnlCardModal: { openOrClosed: 'closed', positionId: undefined },
  accountModal: 'closed',

  setTermsModal: (termsModal) => set({ termsModal }),
  setSettingsModal: (settingsModal) => set({ settingsModal }),
  setDepositModal: (depositModal) => set({ depositModal }),
  setSigningModal: (signingModal) => set({ signingModal }),
  setWithdrawalModal: (withdrawalModal) => set({ withdrawalModal }),
  setLeverageModal: (leverageModal) => set({ leverageModal }),
  setCloseAllPositionsModal: (closeAllPositionsModal) => set({ closeAllPositionsModal }),
  setCancelAllOrdersModal: (cancelAllOrdersModal) => set({ cancelAllOrdersModal }),
  setClosePositionModal: (closePositionModal) => set({ closePositionModal }),
  setMarketsModal: (marketsModal) => set({ marketsModal }),
  setPositionFillsModal: (positionFillsModal) => set({ positionFillsModal }),
  setPositionStopOrderModal: (positionStopOrderModal) => set({ positionStopOrderModal }),
  setTifModal: (tifModal) => set({ tifModal }),
  setConfirmOrderModal: (confirmOrderModal) => set({ confirmOrderModal }),
  setPnlCardModal: (pnlCardModal) => set({ pnlCardModal }),
  setAccountModal: (accountModal) => set({ accountModal }),
}));
